import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

export default function LandingPage() {
  const features = [
    {
      title: "Multi-Organization Support",
      description: "Manage multiple organizations from a single account. Perfect for agencies and consultants.",
    },
    {
      title: "Professional Invoices",
      description: "Create stunning, customizable invoices that reflect your brand identity.",
    },
    {
      title: "Customer Management",
      description: "Keep track of all your customers in one place with detailed profiles and history.",
    },
    {
      title: "Inventory Tracking",
      description: "Monitor your inventory with real-time tracking and comprehensive history logs.",
    },
    {
      title: "Custom Templates",
      description: "Design your own invoice templates or choose from our professional library.",
    },
    {
      title: "Secure & Reliable",
      description: "Your data is protected with enterprise-grade security and automatic backups.",
    },
  ];

  return (
    <>
      <SEO
        title="Home"
        description="Bill Forge - Simple, powerful invoicing for your business. Create professional invoices, manage customers, track inventory, and grow your business with our multi-organization platform. Free invoicing software for small businesses."
        path="/"
      />
      <div className="bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-manrope tracking-tight mb-6">
              Simple, Powerful
              <span className="block text-primary mt-2">Invoicing for Your Business</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Create professional invoices, manage customers, track inventory, and grow your business
              with Bill Forge.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="px-8 py-4 text-lg font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Start Free Trial
              </Link>
              <Link
                to="/pricing"
                className="px-8 py-4 text-lg font-medium border border-border bg-card rounded-lg hover:bg-accent transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold font-manrope mb-6">
                Stop Wasting Time on Manual Invoicing
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Traditional invoicing is slow, error-prone, and unprofessional. Spreadsheets get
                messy, templates break, and tracking payments becomes a nightmare.
              </p>
              <p className="text-lg text-muted-foreground">
                Bill Forge automates the entire process so you can focus on what matters most —
                growing your business.
              </p>
            </div>
            <div className="bg-primary/5 rounded-2xl p-8">
              <h3 className="text-2xl font-bold font-manrope mb-4 text-primary">
                With Bill Forge, You Can:
              </h3>
              <ul className="space-y-3">
                {[
                  "Create invoices in under 60 seconds",
                  "Automate payment reminders",
                  "Track inventory in real-time",
                  "Manage multiple organizations effortlessly",
                  "Customize every invoice to match your brand",
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-primary mr-3">✓</span>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-4">
              Everything You Need to Get Paid
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Powerful features designed to streamline your billing workflow
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-colors"
              >
                <h3 className="text-xl font-bold font-manrope mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
            Ready to Transform Your Invoicing?
          </h2>
          <p className="text-xl opacity-90 mb-10">
            Join thousands of businesses already using Bill Forge to get paid faster.
          </p>
          <Link
            to="/signup"
            className="inline-block px-8 py-4 text-lg font-medium bg-background text-foreground rounded-lg hover:bg-background/90 transition-colors"
          >
            Get Started Free
          </Link>
        </div>
      </section>
    </div>
    </>
  );
}
